import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContentContainer } from "../../../components/layout";
import { useLazyQuery, useQuery } from "@apollo/client";
import { QUERY_LOCKER_TYPE } from "../../../constants/graphql/drop-off-graphql";
import { NEWEST_LOCKER_NAME_QUERY } from "../../../features/stay-time-in-locker/stay-time-query/stay-time-graphql";
import { FILTER_PACKAGES } from "../../../constants/graphql/view-package-history-queries";
import { formatDateYYYYMMDD } from "../../../algorithms/date";
import { getMaxDate } from "../../dashboard/view-package-history";
import GlobalButton from "../../../components/core/global-button";
import GlobalSelect from "../../../components/core/global-select";
import GlobalMultiSelect from "../../../components/core/global-multiselect";
import GlobalPrimereactDateRange from "../../../components/core/global-primereact-daterange";
import { GlobalDataTableSkeleton } from "../../../components/core";
import { Modal } from "../../../components/core";
import PackageStatusTable from "./components/package-table";
import PackageCard from "./components/package-card";

// ── Package state maps ────────────────────────────────────────────────────────
const PACKAGE_STATES_FLAT = {
  110: "Pending",
  118: "Removed",
  121: "Overstay",
  128: "Claimed",
  130: "Take-Back",
  138: "Revoked",
  140: "For RTS",
  141: "RTS Overstay",
  148: "RTS",
  150: "Cancelled",
  158: "Cleared Out",
};

const removeEmptyFields = (obj) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});

const PackageStatusPage = () => {
  const operatorId = JSON.parse(localStorage.getItem("user")).operator_id;
  const maxDate = useMemo(() => getMaxDate(), []);

  // ── State ────────────────────────────────────────────────────────────────────
  const [filterVariables, setFilterVariables] = useState({
    waybillNumber: "",
    doorNumber: "",
    packageId: "",
    lockerTypeId: "-1",
    lockerIds: [],
    dateStart: maxDate,
    dateEnd: new Date(),
    packageStates: [],
  });
  const [lockerType, setLockerType] = useState([]);
  const [lockerNameOptions, setLockerNameOptions] = useState([]);
  const [allLockersSelected, setAllLockersSelected] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [validation, setValidation] = useState({
    icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
    title: "",
    message: "",
    open: false,
  });

  // ── GQL ──────────────────────────────────────────────────────────────────────
  const fetchLockerType = useQuery(QUERY_LOCKER_TYPE);
  const [fetchLockerName, { data: lockerNameData }] = useLazyQuery(
    NEWEST_LOCKER_NAME_QUERY,
    { variables: { operatorId } },
  );
  const [filterPackages, { loading, data }] = useLazyQuery(FILTER_PACKAGES, {
    fetchPolicy: "no-cache",
  });

  // ── Options ──────────────────────────────────────────────────────────────────
  const packageStateOptions = useMemo(
    () =>
      Object.entries(PACKAGE_STATES_FLAT).map(([code, label]) => ({
        value: code,
        label,
      })),
    [],
  );

  // ── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (fetchLockerType.data) {
      setLockerType(
        fetchLockerType.data.lockerTypes.map((el) => ({
          value: el.lockerTypeId,
          label: el.lockerType,
        })),
      );
    }
  }, [fetchLockerType.data]);

  useEffect(() => {
    if (filterVariables.lockerTypeId !== "-1") {
      fetchLockerName({
        variables: { operatorId, lockerTypeId: filterVariables.lockerTypeId },
      });
    }
  }, [filterVariables.lockerTypeId, operatorId, fetchLockerName]);

  useEffect(() => {
    if (lockerNameData) {
      const tempData = lockerNameData.lockerInfo.map((el) => ({
        label: el.lockerName,
        value: el.lockerId,
      }));
      if (!allLockersSelected) {
        tempData.unshift({ value: 0, label: "All Lockers" });
      }
      setLockerNameOptions(tempData);
    }
  }, [lockerNameData, allLockersSelected]);

  useEffect(() => {
    const selected = filterVariables.lockerIds;
    const hasZeroValue = selected.some((item) => item.value === 0);
    if (hasZeroValue) {
      setAllLockersSelected(true);
    } else if (
      selected.length === 0 ||
      selected.length !== lockerNameOptions.length
    ) {
      setAllLockersSelected(false);
    } else {
      setAllLockersSelected(true);
    }
  }, [filterVariables.lockerIds, lockerNameOptions]);

  useEffect(() => {
    if (allLockersSelected && lockerNameOptions.length > 0) {
      setFilterVariables((prev) => ({
        ...prev,
        lockerIds: lockerNameOptions.filter((l) => l.value !== 0),
      }));
    }
  }, [allLockersSelected, lockerNameOptions]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleInputChange = (e, field) => {
    const value = e.target.value;
    if (field === "lockerTypeId") {
      setFilterVariables((prev) => ({
        ...prev,
        [field]: value,
        lockerIds: [],
      }));
    } else {
      setFilterVariables((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleReset = () => {
    setFilterVariables({
      waybillNumber: "",
      doorNumber: "",
      packageId: "",
      lockerTypeId: "-1",
      lockerIds: [],
      dateStart: maxDate,
      dateEnd: new Date(),
      packageStates: [],
    });
    setAllLockersSelected(false);
    setIsClicked(false);
  };

  const validateSearch = () => {
    if (filterVariables.dateStart && filterVariables.dateEnd) {
      if (
        new Date(filterVariables.dateEnd) < new Date(filterVariables.dateStart)
      ) {
        setValidation({
          icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
          title: "Error",
          message: "End date cannot be before start date",
          open: true,
        });
        setIsClicked(false);
        return false;
      }
    }

    if (
      filterVariables.lockerTypeId !== "-1" &&
      filterVariables.lockerIds.length === 0
    ) {
      setValidation({
        icon: `${process.env.PUBLIC_URL}/assets/images/error.png`,
        title: "Error",
        message: "Please select at least one locker name.",
        open: true,
      });
      setIsClicked(false);
      return false;
    }

    return true;
  };

  const handleSearch = useCallback(async () => {
    if (!validateSearch()) return;
    setIsClicked(true);

    const lockerIdValues = filterVariables.lockerIds.map((l) => l.value);
    const packageStateCodes = filterVariables.packageStates.map((s) =>
      parseInt(s.value),
    );

    const criteria = removeEmptyFields({
      operatorId,
      startDate: formatDateYYYYMMDD(filterVariables.dateStart),
      endDate: formatDateYYYYMMDD(filterVariables.dateEnd),
      lockerId: lockerIdValues.length > 0 ? lockerIdValues : undefined,
      waybillNumber: filterVariables.waybillNumber?.trim() || undefined,
      doorId: filterVariables.doorNumber?.trim() || undefined,
      packageId: filterVariables.packageId?.trim() || undefined,
    });

    try {
      await filterPackages({ variables: criteria });
    } catch (error) {
      console.error("Search error:", error);
    }
  }, [filterVariables, operatorId, filterPackages]);

  // ── Search result header ──────────────────────────────────────────────────────
  const searchResultHeader = useMemo(() => {
    const {
      waybillNumber,
      doorNumber,
      lockerIds,
      packageStates,
      dateStart,
      dateEnd,
    } = filterVariables;

    const stateLabel =
      packageStates?.length > 0
        ? packageStates.map((s) => s.label).join(", ")
        : "ALL States";
    const lockerLabel =
      lockerIds?.length > 0
        ? lockerIds.map((l) => l.label).join(", ")
        : "ALL Lockers";

    let sentence = `Search result for ${stateLabel}, ${lockerLabel}`;
    if (waybillNumber) sentence += `, Waybill: ${waybillNumber}`;
    if (doorNumber) sentence += `, Door: ${doorNumber}`;
    if (dateStart && dateEnd) {
      sentence += ` from ${new Date(dateStart).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} to ${new Date(dateEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return sentence;
  }, [filterVariables]);

  // ── Filter packages by selected states (client-side since backend filterPackages doesn't support it yet) ──
  const filteredData = useMemo(() => {
    const raw = data?.filterPackages ?? [];
    if (filterVariables.packageStates.length === 0) return raw;
    const selectedCodes = new Set(
      filterVariables.packageStates.map((s) => parseInt(s.value)),
    );
    return raw.filter((p) => selectedCodes.has(p.packageStateNo));
  }, [data, filterVariables.packageStates]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <ContentContainer>
      <div className="font-bold text-lg">Package Status</div>
      <div className="font-normal text-sm">
        Search and filter packages by status across all lockers.
      </div>

      {/* Search filters */}
      <div className="grid grid-cols-12 md:grid-cols-10 gap-2 mb-2 pt-4">
        <div className="col-span-12 md:col-span-7">
          <h2 className="font-bold w-full">Package Details:</h2>
          <div className="grid grid-cols-10 items-center space-y-1">
            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Package State:</p>
            </div>
            <GlobalMultiSelect
              items={packageStateOptions}
              name="packageStates"
              className="col-span-10 md:col-span-6"
              value={filterVariables.packageStates}
              placeholder="Select package state..."
              onChange={(e) => handleInputChange(e, "packageStates")}
            />

            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Waybill Number:</p>
            </div>
            <div className="col-span-10 md:col-span-6">
              <input
                className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-primaryYellow"
                placeholder="XXXXXXXXXXXXXXXX"
                onChange={(e) => handleInputChange(e, "waybillNumber")}
                value={filterVariables.waybillNumber}
              />
            </div>

            <div className="col-span-4">
              <p className="text-base">Door Number:</p>
            </div>
            <div className="col-span-6">
              <input
                className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-primaryYellow"
                onChange={(e) => handleInputChange(e, "doorNumber")}
                value={filterVariables.doorNumber}
                maxLength={3}
              />
            </div>
          </div>

          <h2 className="font-bold w-full pt-2">Locker:</h2>
          <div className="grid grid-cols-10 items-center space-y-1">
            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Locker Type:</p>
            </div>
            <GlobalSelect
              items={[
                { value: "-1", label: "-- ALL Locker Types --" },
                ...lockerType,
              ]}
              parentClassName="col-span-10 md:col-span-6"
              className="w-full"
              name="lockerTypeId"
              value={filterVariables.lockerTypeId}
              onChange={(e) => handleInputChange(e, "lockerTypeId")}
            />

            <div className="col-span-10 md:col-span-4">
              <p className="text-base">Locker Name:</p>
            </div>
            <GlobalMultiSelect
              items={lockerNameOptions}
              name="lockerId"
              className="col-span-10 md:col-span-6"
              value={filterVariables.lockerIds}
              placeholder={
                filterVariables.lockerTypeId === "-1"
                  ? "ALL Lockers"
                  : "Select..."
              }
              disabled={filterVariables.lockerTypeId === "-1"}
              onChange={(e) => handleInputChange(e, "lockerIds")}
            />
          </div>

          <h2 className="font-bold w-full pt-2">Package Dropoff Date:</h2>
          <div className="grid grid-cols-8 px-4 items-center space-y-1">
            <GlobalPrimereactDateRange
              onChange={handleInputChange}
              startDateName="dateStart"
              endDateName="dateEnd"
              readOnlyInput={true}
              className="custom-calendar-button col-span-8 md:col-span-4"
              max={new Date()}
              min={new Date("2021-01-01")}
              endDate={filterVariables.dateEnd}
              startDate={filterVariables.dateStart}
            />
          </div>

          <div className="grid grid-cols-4">
            <div className="flex mt-4 items-center space-x-1">
              <GlobalButton
                icon="pi pi-undo"
                parentClassName="col-span-1"
                buttonClassName="flex p-2 items-center gap-2 border border-primary-primaryYellow bg-white text-black hover:bg-secondary-lightestGray font-normal"
                onClick={handleReset}
              >
                <div>Reset</div>
              </GlobalButton>
              <GlobalButton
                parentClassName="col-span-1"
                onClick={handleSearch}
                icon="pi pi-search"
                buttonClassName="flex p-2 mx-2 items-center gap-2 bg-primary-primaryYellow text-secondary-purpleGray border-0 font-semibold hover:bg-primary-primaryYellowDark"
                label="Search"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {isClicked && (
        <div className="space-y-4 pt-6">
          {/* Desktop table */}
          {loading ? (
            <div className="hidden md:block">
              <GlobalDataTableSkeleton totalColumns={5} />
            </div>
          ) : (
            <div className="hidden md:block mt-4 border rounded shadow-sm">
              <PackageStatusTable
                items={filteredData}
                searchResultHeader={searchResultHeader}
              />
            </div>
          )}

          {/* Mobile cards */}
          <div className="block md:hidden mt-4">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-gray-100 animate-pulse rounded-xl h-28"
                  />
                ))}
              </div>
            ) : (
              <>
                {filteredData && filteredData.length > 0 && (
                  <div className="mb-4 bg-primary-primaryYellowLight p-3 rounded-lg shadow-sm">
                    <div className="text-sm font-bold text-secondary-purpleGray">
                      Total Packages: {filteredData.length}
                    </div>
                    <div className="text-center font-montserrat text-sm font-semibold text-secondary-purpleGray rounded px-4 py-2">
                      {searchResultHeader}
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  {filteredData?.map((item, idx) => (
                    <PackageCard
                      key={item.packageId || idx}
                      row={{
                        packageId: item.packageId,
                        userAccountId: item.userAccount?.userAccountId,
                        partnerAgentId: item.partnerAgentId,
                        lockerLocation: item.lockerLocation,
                        partnerAgentMobile: item.partnerAgentMobile,
                        waybillNumber: item.waybillNumber,
                        packageDate: item.packageDate,
                        packageStateNo: item.packageStateNo,
                        packageStateLabel:
                          PACKAGE_STATES_FLAT[item.packageStateNo] ?? "Unknown",
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        icon={validation.icon}
        title={validation.title}
        message={validation.message}
        open={validation.open}
        onClose={() => setValidation((prev) => ({ ...prev, open: false }))}
      />
    </ContentContainer>
  );
};

export default PackageStatusPage;
